import {
  Site,
  SiteFilterInputObject,
  SitesQueryVariables,
  useSitesIdsLazyQuery,
  useSitesIdsQuery,
  useSitesQuery,
  TaskFilterInputObject,
} from 'graphql-common';
import _get from 'lodash/get';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { ListQueryHookProps, ListQueryHookResult } from '../types';

export default function useSitesQueryHook({
  additionalFilters,
  paginationParams,
  searchQuery,
  skip,
  skipGetAllIds = true,
  usePrevious = true,
  fetchPolicy = 'cache-and-network',
  includeFullData = true,
  includePerformanceDataByTaskDeadlinesMissed = false,
  includePerformanceDataByTaskResults = false,
  taskFilters,
}: ListQueryHookProps & {
  includeFullData?: boolean;
  includePerformanceDataByTaskDeadlinesMissed?: boolean;
  includePerformanceDataByTaskResults?: boolean;
  taskFilters?: TaskFilterInputObject[];
}): ListQueryHookResult<Site> {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );
  const sitesQueryVariables: SitesQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    includeFullData,
    includePerformanceDataByTaskDeadlinesMissed,
    includePerformanceDataByTaskResults,
  };
  if (orderingField && orderingDirection) {
    sitesQueryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const filters: SiteFilterInputObject[] = [];
  if (searchQuery) {
    filters.push(
      ...createSearchFilters(searchQuery, [
        { fieldName: 'areasCode' },
        { fieldName: 'areasName' },
        { fieldName: 'code' },
        { fieldName: 'name' },
        { fieldName: 'userFullName' },
        { fieldName: 'sectorName' },
      ]),
    );
  }
  if (additionalFilters?.length) {
    filters.push(...additionalFilters);
  }
  if (filters.length) {
    sitesQueryVariables.filters = filters;
  }
  if (taskFilters?.length) {
    sitesQueryVariables.taskFilters = taskFilters;
  }
  const sitesQuery = useSitesQuery({
    fetchPolicy,
    variables: sitesQueryVariables,
    skip,
  });
  const allIdsQuery = useSitesIdsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      onlyIds: true,
      filters: sitesQueryVariables.filters,
    },
    skip: skipGetAllIds,
  });
  const [fetchIdsQuery] = useSitesIdsLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const fetchIds = () =>
    fetchIdsQuery({
      variables: {
        onlyIds: true,
        filters: sitesQueryVariables.filters,
      },
    });
  const { loading, data, previousData, error, refetch } = sitesQuery;
  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'data.collection', []) as Site[]);
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 0);

  return {
    allIds: _get(allIdsQuery, ['data', 'data', 'collection'], []),
    collection,
    error,
    fetchIds,
    firstLoading,
    loading,
    refetch,
    refetchAllIds: allIdsQuery.refetch,
    totalCount,
    totalPages,
  };
}
